import React, { useEffect, useMemo, useState } from "react";
import { Search, FlaskConical, ExternalLink, ShieldCheck } from "lucide-react";
import { GradeBadge, WadaBadge } from "@/components/Badges";
import { useLang } from "@/context/LanguageContext";
import api from "@/lib/api";

const GOALS = ["strength", "endurance", "energy", "recovery"];
const GRADES = ["A", "B", "C", "D"];

export default function Library({ embedded = false }) {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [goal, setGoal] = useState("");
  const [grade, setGrade] = useState("");

  useEffect(() => {
    api.get("/ingredients").then(({ data }) => setItems(data.ingredients || []));
  }, []);

  const filtered = useMemo(() => items.filter((i) => {
    if (q && !`${i.name} ${i.aka}`.toLowerCase().includes(q.toLowerCase())) return false;
    if (goal && !(i.goals || []).includes(goal)) return false;
    if (grade && i.evidence_grade !== grade) return false;
    return true;
  }), [items, q, goal, grade]);

  return (
    <div>
      {!embedded && <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{t("lib.title")}</h1>
        <p className="mt-1 text-sm text-gray-600">{t("lib.sub")}</p>
      </div>}

      <div className="mb-6 rounded-2xl border border-gray-200 bg-white p-4">
        <div className="relative mb-4">
          <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input data-testid="library-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("lib.search")}
            className="w-full rounded-full border border-gray-200 py-2.5 pl-11 pr-4 text-sm outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100" />
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-bold uppercase tracking-wider text-gray-400">{t("lib.goal")}:</span>
            <button onClick={() => setGoal("")} className={`rounded-full px-3 py-1.5 text-xs font-semibold ${!goal ? "bg-emerald-500 text-white" : "bg-gray-100 text-gray-600"}`}>{t("lib.all")}</button>
            {GOALS.map((g) => (
              <button key={g} data-testid={`library-goal-filter-${g}`} onClick={() => setGoal(g === goal ? "" : g)}
                className={`rounded-full px-3 py-1.5 text-xs font-semibold ${goal === g ? "bg-emerald-500 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>{t(`goal.${g}`)}</button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2 sm:ml-auto">
            <span className="text-xs font-bold uppercase tracking-wider text-gray-400">{t("lib.grade")}:</span>
            {GRADES.map((g) => (
              <button key={g} data-testid={`library-evidence-filter-${g}`} onClick={() => setGrade(g === grade ? "" : g)}
                className={`h-7 w-7 rounded-full text-xs font-bold ${grade === g ? "bg-emerald-500 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>{g}</button>
            ))}
          </div>
        </div>
      </div>

      <div className="mb-6 flex items-start gap-2 rounded-xl border border-emerald-100 bg-emerald-50/60 p-3 text-xs leading-relaxed text-emerald-800">
        <ShieldCheck size={14} className="mt-0.5 shrink-0" /> {t("lib.wadaNote")}
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {filtered.map((i) => (
          <div key={i.slug} data-testid={`library-ingredient-card-${i.slug}`} className="rounded-2xl border border-gray-200 bg-white p-6 transition-shadow hover:shadow-md">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-lg font-bold text-gray-800">{i.name}</h3>
                <p className="text-xs text-gray-400">{i.aka}</p>
              </div>
              <GradeBadge grade={i.evidence_grade} />
            </div>
            <p className="mt-3 text-sm leading-relaxed text-gray-600">{i.summary}</p>
            <div className="mt-4 flex flex-wrap items-center gap-2">
              {(i.goals || []).map((g) => (
                <span key={g} className="rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">{t(`goal.${g}`)}</span>
              ))}
              <WadaBadge status={i.wada_status} />
            </div>
            <div className="mt-4 space-y-1.5 border-t border-gray-100 pt-4 text-sm">
              <p><span className="font-semibold text-gray-700">{t("lib.dosage")}:</span> <span className="text-gray-600">{i.dosage}</span></p>
              <p><span className="font-semibold text-gray-700">{t("lib.safety")}:</span> <span className="text-gray-600">{i.safety_notes}</span></p>
              <p className="flex items-center gap-1.5 text-gray-500"><FlaskConical size={14} /> {i.clinical_studies}+ {t("lib.studies")}</p>
            </div>
            <a href={`https://pubmed.ncbi.nlm.nih.gov/?term=${encodeURIComponent(i.name)}`} target="_blank" rel="noreferrer"
              className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-600 hover:underline">
              {t("lib.viewPubmed")} <ExternalLink size={14} />
            </a>
          </div>
        ))}
      </div>
    </div>
  );
}
